import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import LegacyColumnFilterDropdown from 'nav/expanded/LegacyColumnFilterDropdown';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ButtonSize} from 'ui-library/buttons/Button';

import {DisplayConfigWithoutReviewed} from './AttachmentConstants';
import {SpecialColumn} from './ColumnConstants';
import {getSpecialColumns} from './Columns';

const SHOW_ALL_SPECIAL_COLUMNS = _.object(getSpecialColumns().map(({isHiddenKey}) => [isHiddenKey, false]));
const HIDE_ALL_SPECIAL_COLUMNS = _.object(getSpecialColumns().map(({isHiddenKey}) => [isHiddenKey, true]));

export interface DisplayConfigDropdownProps {
  displayConfig: DisplayConfigWithoutReviewed;
  schema: AssaySchema;
  updateDisplayConfigHandler: (schemaId: string, displayConfig: DisplayConfigWithoutReviewed) => void;
}

class DisplayConfigDropdown extends React.Component<DisplayConfigDropdownProps> {
  _getConfigurableFields(): Array<SchemaField> {
    const primaryLinkField = getPrimaryLinkField(this.props.schema);
    return this.props.schema.fields.filter((field) => field !== primaryLinkField);
  }

  _handleToggleAllFilters = (): void => {
    if (
      this.props.displayConfig.hiddenFields.length === 0 &&
      _.isMatch(this.props.displayConfig, SHOW_ALL_SPECIAL_COLUMNS)
    ) {
      // Hide all fields
      this.props.updateDisplayConfigHandler(this.props.schema.id, {
        ...this.props.displayConfig,
        ...HIDE_ALL_SPECIAL_COLUMNS,
        hiddenFields: this._getConfigurableFields().map((field) => field.name),
      });
    } else {
      // Show all fields
      this.props.updateDisplayConfigHandler(this.props.schema.id, {
        ...this.props.displayConfig,
        ...SHOW_ALL_SPECIAL_COLUMNS,
        hiddenFields: [],
      });
    }
  };

  _toggleSpecialColumnDisplayConfig(specialColumn: SpecialColumn): void {
    const {isHiddenKey} = specialColumn;
    this.props.updateDisplayConfigHandler(this.props.schema.id, {
      ...this.props.displayConfig,
      [isHiddenKey]: !this.props.displayConfig[isHiddenKey],
    });
  }

  _toggleSchemaFieldVisibility(field: SchemaField): void {
    if (this.props.displayConfig.hiddenFields.includes(field.name)) {
      this.props.updateDisplayConfigHandler(this.props.schema.id, {
        ...this.props.displayConfig,
        // eslint-disable-next-line no-restricted-syntax
        hiddenFields: _.without(this.props.displayConfig.hiddenFields, field.name),
      });
    } else {
      this.props.updateDisplayConfigHandler(this.props.schema.id, {
        ...this.props.displayConfig,
        hiddenFields: this.props.displayConfig.hiddenFields.concat(field.name),
      });
    }
  }

  _handleToggleFilter = (index: number): void => {
    const {field, specialColumn} = this._getColumnFilters()[index];
    if (specialColumn) {
      this._toggleSpecialColumnDisplayConfig(specialColumn);
    } else {
      assert(field != null, 'Column filter must be for a special column or a schema field');
      this._toggleSchemaFieldVisibility(field);
    }
  };

  _getColumnFilters(): Array<{
    specialColumn?: SpecialColumn;
    field?: SchemaField;
    header: string;
    hidden: boolean;
  }> {
    const filters = [];
    for (const specialColumn of getSpecialColumns()) {
      filters.push({
        specialColumn,
        header: specialColumn.name,
        hidden: this.props.displayConfig[specialColumn.isHiddenKey],
      });
    }
    for (const field of this._getConfigurableFields()) {
      filters.push({
        field,
        header: field.displayName,
        hidden: this.props.displayConfig.hiddenFields.includes(field.name),
      });
    }
    return filters;
  }

  override render(): JSX.Element {
    return (
      <LegacyColumnFilterDropdown
        buttonSize={ButtonSize.MINI}
        onToggleAllFilters={this._handleToggleAllFilters}
        onToggleFilter={this._handleToggleFilter}
        columns={this._getColumnFilters().map(({header, hidden}) => ({header, hidden}))}
      />
    );
  }
}

export default DisplayConfigDropdown;
